import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import React, {
  useEffect,
  useState,
  KeyboardEvent,
  useRef,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import Input from "../../components/Commons/Input";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useGlobalState } from "../../contexts/GlobalContext";
import { authHelper } from "../../utils/helpers";
import { AxiosResponse } from "axios";
import { Http, Regex, Routes } from "../../utils/constants";
import SolidArrow from "../../components/svg/SolidArrow";
import routes from "../../utils/constants/routes";
import { Select } from "../../components/Commons/Select";
import {
  checkEmailExists,
  createBasicUser,
  updateUserMetadata,
} from "../../utils/helpers/api";
import { occupationSelect } from "../../utils/constants/constant";
import { useNotificationTimestamp } from "../../hooks/features/notification/useNotificationTimestamp";
import ErrorMsg from "../../components/Commons/ErrorMsg";
import axios, { AxiosError } from "axios";

interface IRegister {
  isCheck: boolean;
  isExistsEmail: boolean;
  email?: string;
  password?: string;
  passwordConfirm?: string;
}

const RegisterFormSimple: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setStateValue, globalState, setGlobalState } = useGlobalState();
  const { addTimestamp } = useNotificationTimestamp();
  const { editedRegistrationData } = location.state ?? {};
  const [state, setState] = useState<IRegister>({
    isCheck: false,
    isExistsEmail: false,
    email: "",
    password: "",
    passwordConfirm: "",
  });
  const { isCheck, email, password, passwordConfirm, isExistsEmail } = state;
  const [isAuthenticated, setIsAuthenticated] = useState(authHelper.isAuth());
  const checkboxRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const pwdRef = useRef<HTMLInputElement>(null);
  const pwdConfirmRef = useRef<HTMLInputElement>(null);
  const birthRef = useRef<HTMLDivElement>(null);
  const occupationRef = useRef<HTMLDivElement>(null);
  const [birthYear, setBirthYear] = useState("0");
  const [birthMonth, setBirthMonth] = useState("0");
  const [birthDay, setBirthDay] = useState("0");
  const [occupationCode, setOccupationCode] = useState("null");
  const [days, setDays] = useState<{ value: string; label: string }[]>([]);
  const [checkboxValues, setCheckboxValues] = useState({
    sendenkaigiInfo: true,
    classInfo: false,
    contestsAwards: true,
    advertimes: true,
    digitalMagazine: false,
    editorialSendenkaigi: false,
    editorialHansokukaigi: false,
    editorialKohokaigi: false,
    editorialBrain: false,
    editorialBooks: true,
    adPlacementSponsorship: false,
    jobChangeInfo: false,
  });

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    passwordConfirm: "",
    isValid: false,
  });
  const [error, setError] = useState({
    day: "",
    month: "",
    year: "",
    occupationCode: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const returnTo = params.get("returnto");
    const external = params.get("external");

    if (returnTo) {
      localStorage.setItem("returnadvertimes", returnTo);
      localStorage.setItem("trackingInflowRoute",returnTo);
    }

    localStorage.setItem("external", external ?? "");
  }, [location.search]);

  // Get saved form data from session storage
  useEffect(() => {
    const savedData = sessionStorage.getItem("formData");
    if (savedData) {
      setState(JSON.parse(savedData));
      sessionStorage.removeItem("formData");
    }
  }, []);
  useEffect(() => {
    if (editedRegistrationData) {
      setState((prevState) => ({
        ...prevState,
        email: editedRegistrationData.email,
        isCheck: editedRegistrationData.isCheck,
      }));
    }
  }, [editedRegistrationData]);

  useEffect(() => {
    const year = parseInt(birthYear, 10);
    const month = parseInt(birthMonth, 10);

    if (!isNaN(year) && !isNaN(month) && year !== 0 && month !== 0) {
      const maxDays = getDaysInMonth(year, month);
      const dayOptions = [
        { value: "0", label: "日" }, // chỉ chữ 日 thôi
        ...Array.from({ length: maxDays }, (_, i) => ({
          value: (i + 1).toString(),
          label: `${i + 1}日`,
        })),
      ];
      setDays(dayOptions);
    } else {
      setDays([
        { value: "0", label: "日" }, // chỉ chữ 日 thôi
        ...Array.from({ length: 31 }, (_, i) => ({
          value: (i + 1).toString(),
          label: `${i + 1}日`,
        })),
      ]);
    }
  }, [birthYear, birthMonth]);

  const access_token = authHelper.accessToken();
  if (isAuthenticated && access_token) {
    return (
      <Box className="page__register--result d-flex justify-content-center mt-4">
        <Link to={Routes.USER} className="btn btn-primary center">
          {t("features.register.backToHome")}
        </Link>
      </Box>
    );
  }

  const handleChangeBirth = (type: "year" | "month" | "day", value: string) => {
    setError((prev) => ({
      ...prev,
      [type]: value && value !== "0" ? "" : t("validation.selectRequired"),
    }));
    if (type === "year") {
      setBirthYear(value);
      setBirthDay("0");
    }
    if (type === "month") {
      setBirthMonth(value);
      setBirthDay("0");
    }
    if (type === "day") {
      setBirthDay(value);
    }
  };

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [
      { value: "0", label: "年" }, // chỉ chữ 年 thôi
      ...Array.from({ length: 100 }, (_, i) => ({
        value: (currentYear - i).toString(),
        label: `${currentYear - i}年`,
      })),
    ];
  }, []);

  const months = useMemo(() => {
    return [
      { value: "0", label: "月" }, // chỉ chữ 月 thôi
      ...Array.from({ length: 12 }, (_, i) => ({
        value: (i + 1).toString(),
        label: `${i + 1}月`,
      })),
    ];
  }, []);

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate(); // tháng bắt đầu từ 1
  };

  const handlerCheckEmail = async (): Promise<boolean> => {
    setStateValue("loader", true);
    try {
      const lowerCaseEmail = (email ?? "").toLowerCase();
      const result = await checkEmailExists(lowerCaseEmail);
      const { exists } = result.data;
      setState((prevState) => ({
        ...prevState,
        isExistsEmail: exists,
      }));
      return exists;
    } catch (error) {
      console.error("Error checking email:", error);
      return true;
    } finally {
      setStateValue("loader", false);
    }
  };

  const handlerScrollToTop = () => {
    document.documentElement.style.scrollBehavior = "auto";
    setTimeout(() => window.scrollTo(0, 0), 5);
    setTimeout(
      () => (document.documentElement.style.scrollBehavior = "smooth"),
      100,
    );
  };

  const validateForm = () => {
    let isValidate = true;
    let errorEmailMess = "";
    let errorPassMess = "";
    let errorPassConfirmMess = "";

    let errorYear = "";
    let errorMonth = "";
    let errorDay = "";
    let errorOccupationCode = "";

    const checkRulePwd = (pwd: string) => {
      const upperCase = /[A-Z]/.test(pwd);
      const lowerCase = /[a-z]/.test(pwd);
      const number = /[0-9]/.test(pwd);
      const specialChar = /[@$!%*?&]/.test(pwd);
      const categories = [upperCase, lowerCase, number, specialChar].filter(
        Boolean,
      ).length;

      return categories < 3 ? false : true;
    };

    if (!email) {
      isValidate = false;
      setState((prevState) => ({
        ...prevState,
        isExistsEmail: false,
      }));
      errorEmailMess = "メールアドレス欄は必須です。";
    } else if (email.length > 256) {
      isValidate = false;
      errorEmailMess = "メールアドレス欄は256文字を超えてはいけません。";
    } else if (!Regex.RULE.EMAIL.test(email)) {
      isValidate = false;
      errorEmailMess = "メールアドレスを正しく入力してください";
    } else {
      errorEmailMess = "";
    }

    if (!password) {
      isValidate = false;
      errorPassMess = "パスワード欄は必須です。";
    } else if (password.length < 8 || !checkRulePwd(password)) {
      isValidate = false;
      errorPassMess =
        "大文字(A-Z)、小文字(a-z)、数字(0-9)、記号（!@#$%^&*）のうち３種類を使って、８桁以上にしてください。";
    } else if (password.length > 50) {
      isValidate = false;
      errorPassMess = "パスワード欄は50文字を超えてはいけません。";
    } else {
      errorPassMess = "";
    }

    if (!passwordConfirm) {
      isValidate = false;
      errorPassConfirmMess = "パスワード:確認欄は必須です。";
    } else if (passwordConfirm.length < 8 || !checkRulePwd(passwordConfirm)) {
      isValidate = false;
      errorPassConfirmMess =
        "大文字(A-Z)、小文字(a-z)、数字(0-9)、記号（!@#$%^&*）のうち３種類を使って、８桁以上にしてください。";
    } else if (passwordConfirm.length > 50) {
      isValidate = false;
      errorPassConfirmMess = "パスワード:確認欄は50文字を超えてはいけません。";
    } else if (password !== passwordConfirm) {
      errorPassConfirmMess =
        "パスワード:確認欄が新しいパスワードと一致しません。";
      isValidate = false;
    } else {
      errorPassConfirmMess = "";
    }

    if (!birthYear || birthYear === "0") {
      errorYear = t("validation.selectRequired");
      isValidate = false;
    }
    if (!birthMonth || birthMonth === "0") {
      errorMonth = t("validation.selectRequired");
      isValidate = false;
    }
    if (!birthDay || birthDay === "0") {
      errorDay = t("validation.selectRequired");
      isValidate = false;
    }

    // Occupation
    if (!occupationCode || occupationCode === "null") {
      errorOccupationCode = t("validation.selectRequired");
      isValidate = false;
    }

    setErrors({
      email: errorEmailMess,
      password: errorPassMess,
      passwordConfirm: errorPassConfirmMess,
      isValid: isValidate,
    });

    setError({
      year: errorYear,
      month: errorMonth,
      day: errorDay,
      occupationCode: errorOccupationCode,
    });

    return isValidate;
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();

    handlerScrollToTop();

    const isFormValid = validateForm();

    const lowerCaseEmail = (email ?? "").toLowerCase();

    // Save form data to local storage to use in the next step
    sessionStorage.setItem(
      "formData",
      JSON.stringify({ email: lowerCaseEmail, isCheck: isCheck }),
    );
    if (errors.email) {
      emailRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      emailRef.current?.focus();
    } else if (errors.password) {
      pwdRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      pwdRef.current?.focus();
    } else if (errors.passwordConfirm) {
      pwdConfirmRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      pwdConfirmRef.current?.focus();
    } else if (error.year || error.month || error.day) {
      birthRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } else if (error.occupationCode) {
      occupationRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    if (isFormValid) {
      const isEmailExists = await handlerCheckEmail();
      if (!isEmailExists) {
        await handleUpdate(lowerCaseEmail, password ?? "");
      }
    }
  };

  const handleUpdate = async (email: string, password: string) => {
  setStateValue("isSaving", true);
  let currentUserId: string | null = null;

  try {
    let dataCreate: IUserMetaDataApi = {};
    dataCreate.user_status = 4;
    dataCreate.authenticated = 0;
    dataCreate.newsletterSubscription = {
      advertimes: checkboxValues.advertimes,
      contestsAwards: checkboxValues.contestsAwards,
      editorialBooks: checkboxValues.editorialBooks,
      sendenkaigiInfo: checkboxValues.sendenkaigiInfo,
      classInfo: checkboxValues.classInfo,
      digitalMagazine: checkboxValues.digitalMagazine,
      editorialSendenkaigi: checkboxValues.editorialSendenkaigi,
      editorialHansokukaigi: checkboxValues.editorialHansokukaigi,
      editorialKohokaigi: checkboxValues.editorialKohokaigi,
      editorialBrain: checkboxValues.editorialBrain,
      adPlacementSponsorship: checkboxValues.adPlacementSponsorship,
      jobChangeInfo: checkboxValues.jobChangeInfo,
    };
    dataCreate.profile = {
      birth: {
        year: parseInt(birthYear, 10),
        month: parseInt(birthMonth, 10),
        day: parseInt(birthDay, 10),
      },
      occupationCode: parseInt(occupationCode, 10),
    };

    const originalUser: IUserMetaDataApi = {};
    dataCreate = addTimestamp(originalUser, dataCreate);

    const result = (await createBasicUser(
      email,
      password,
      dataCreate,
    )) as AxiosResponse;

    if (result?.status === Http.CREATED_CODE) {
      localStorage.removeItem("loginCallBackUrl");
      sessionStorage.removeItem("formData");

      const response = (await checkEmailExists(email)) as AxiosResponse;
      if (response?.data?.user) {
        const { user_id, user_metadata } = response.data.user;
        currentUserId = user_id;

        const raw = localStorage.getItem("transition_source");
        let transitionSourceValue: string | null = null;
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            transitionSourceValue = (parsed && typeof parsed === "object" && "value" in parsed) ? parsed.value : raw;
          } catch {
            transitionSourceValue = raw;
          }
        }

        if (transitionSourceValue && user_id) {
          const dataUpdate: IUserMetaDataApi = user_metadata ?? {};
          dataUpdate.transition_source = transitionSourceValue;
          await updateUserMetadata(email, user_id, dataUpdate);
        }
      }

      navigate(Routes.EMAIL_SENT, {
        state: { 
          email, 
          password, 
          isRegisterSimple: true, 
          user_id: currentUserId 
        },
      });

      setGlobalState({
        ...globalState,
        user: {
          ...globalState.user,
          user_metadata: dataCreate,
        },
      });
    }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.response?.data?.code === "INVALID_ADDITIONAL_INFO") {
        navigate(Routes.CREATEUSERERROR);
      } else if (err.response?.data?.code === "USER_ALREADY_EXISTS") {
        const response = (await checkEmailExists(email, true)) as AxiosResponse;
        if (response?.data?.user) {
          currentUserId = response.data.user.user_id;
        }

        setState((prevState) => ({
          ...prevState,
          isExistsEmail: true,
        }));

      } else if (err.response?.data?.code === "MISSING_REQUIRED_FIELDS") {
        navigate(Routes.CREATEUSERERROR);
      }
    }
  } finally {
    setStateValue("isSaving", false);
  }
};

  const handlerChangeValue = (field: string, value: string) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLButtonElement>,
  ) => {
    if (e.key === "Enter") {
      if (!isCheck) {
        checkboxRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        checkboxRef.current?.focus();
      } else {
        handleSubmit();
      }
    }
  };

  return (
    <div className="register-page">
      <section>
        <div className="rp-head">
          <div className="step-info">
            <div className="page-title">
              <span>新規会員登録</span>
            </div>
            <div className="d-flex description-wrapper">
              <p className="-description">
                メールアドレス・パスワードを入力し、利用規約をご確認の上「確認」ボタンを押してください。
              </p>
              <div className="-to-login">
                <Link to={routes.LOGIN} className="cus-decoration">
                  <span>すでに会員の方</span>
                  <span className="span-icon">
                    <SolidArrow />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <form onSubmit={handleSubmit}>
        <section className="register-form">
          <div>
            <div className="form-items">
              <div className="form-item">
                <dt className="fi-label">
                  <label>
                    <span className="text">メールアドレス</span>
                    <span className="item_required">
                      {t("general.required")}
                    </span>
                  </label>
                </dt>
                <dd className="fi-control">
                  <Input
                    validateMess={errors.email}
                    className="mb-2"
                    inputRef={emailRef}
                    error={errors.email}
                    // onKeyDown={handleKeyDown}
                    placeholder="taro@example.com"
                    name="email"
                    id="email"
                    value={state.email}
                    onChange={(value) => {
                      handlerChangeValue("email", value);
                    }}
                    withChecked={true}
                  />
                  {!errors.email && (
                    <span className="txt-14">※ビジネスメールを推奨します</span>
                  )}

                  {isExistsEmail && (
                    <div className="text-danger">
                      <span>
                        このメールアドレスはすでに登録済みです。
                        <a
                          style={{ textDecoration: "none" }}
                          href={Routes.LOGIN}
                        >
                          こちら
                        </a>
                        からログインをお願いします。
                      </span>
                      <br />
                      <span>
                        ※パスワードを忘れた方は
                        <a
                          style={{ textDecoration: "none" }}
                          href={Routes.PASSWORDREMIND}
                        >
                          こちら
                        </a>
                        から再発行をお願いします。
                      </span>
                      <br />
                      <span>
                        ※本人確認メールが届かない場合は
                        <a
                          style={{ textDecoration: "none" }}
                          href={`${
                            Routes.EMAIL_SENT
                          }?email=${encodeURIComponent(email ?? "")}`}
                        >
                          こちら
                        </a>
                        から再送要求をしてください。
                      </span>
                    </div>
                  )}
                </dd>
              </div>
              <div className="form-item">
                <dt className="fi-label">
                  <label>
                    <span className="text">パスワード</span>
                    <span className="item_required">
                      {t("general.required")}
                    </span>
                  </label>
                </dt>
                <dd className="fi-control">
                  <Input
                    type="password"
                    inputRef={pwdRef}
                    // onKeyDown={handleKeyDown}
                    className="mb-2"
                    error={errors.password}
                    placeholder="入力してください"
                    name="password"
                    id="password"
                    value={state.password}
                    onChange={(value) => {
                      handlerChangeValue("password", value);
                    }}
                    withChecked={true}
                  />
                  {!errors.password && (
                    <span className="txt-14">
                      ※大文字(A-Z)、小文字(a-z)、数字(0-9)、記号（!@#$%^&*）のうち３種類を使って、８桁以上にしてください。
                    </span>
                  )}
                </dd>
              </div>
              <div className="form-item">
                <dt className="fi-label">
                  <label>
                    <span className="text">
                      {" "}
                      {t("features.register.confirm_password_help_text")}
                    </span>
                  </label>
                </dt>
                <dd className="fi-control">
                  <Input
                    type="password"
                    inputRef={pwdConfirmRef}
                    // onKeyDown={handleKeyDown}
                    error={errors.passwordConfirm}
                    placeholder=""
                    id="passwordConfirm"
                    value={state.passwordConfirm}
                    onChange={(value) => {
                      handlerChangeValue("passwordConfirm", value);
                    }}
                    withChecked={true}
                  />
                </dd>
              </div>
            </div>
          </div>

          <div className={`-body-item -item_edit`} ref={birthRef}>
            <dt className="-middle">
              <label>
                <span className="-span">
                  {t("features.userSetting.basicInfo.birthDay")}
                </span>
                <span className="-item_required">{t("general.required")}</span>
              </label>
            </dt>
            <dd>
              <div className="form-col -col2">
                <div className="-form-col_item">
                  <div className="form-input d-flex align-items-center">
                    <Select
                      name="birthday_year"
                      id="birthday_year"
                      className="custom-select"
                      options={years}
                      onChange={(value) => handleChangeBirth("year", value)}
                      value={birthYear || ""}
                      customSelect={true}
                    />
                    <Select
                      name="birthday_month"
                      id="birthday_month"
                      className="custom-select"
                      options={months}
                      onChange={(value) => handleChangeBirth("month", value)}
                      value={birthMonth || ""}
                      customSelect={true}
                    />
                    <Select
                      name="birthday_day"
                      id="birthday_day"
                      className="custom-select"
                      options={days}
                      onChange={(value) => handleChangeBirth("day", value)}
                      value={birthDay || ""}
                      customSelect={true}
                    />
                  </div>
                </div>
              </div>

              <ErrorMsg
                error={
                  error.day
                    ? error.day
                    : error.month
                      ? error.month
                      : error.year
                        ? error.year
                        : ""
                }
                className="invalid-danger"
              />
            </dd>
          </div>

          <div className={`-body-item -item_edit`} ref={occupationRef}>
            <dt className="-middle">
              <label>
                <span className="-span">
                  {t("features.userSetting.basicInfo.job")}
                </span>

                <span className="-item_required">{t("general.required")}</span>
              </label>
            </dt>
            <dd>
              <div className="form-col -col">
                <div className="-form-col_item">
                  <Select
                    value={occupationCode}
                    className="custom-select"
                    name="occupationCode"
                    id="occupationCode"
                    classNameWrapper="col-md-5 min-w-250"
                    onChange={(value) => {
                      setOccupationCode(value);

                      setError((prev) => ({
                        ...prev,
                        occupationCode:
                          value && value !== "null"
                            ? ""
                            : t("validation.selectRequired"),
                      }));
                    }}
                    // error={!!errors.occupationCode}
                    options={occupationSelect}
                    customSelect={true}
                  />
                  <ErrorMsg
                    error={error.occupationCode}
                    className="invalid-danger"
                  />
                </div>
              </div>
            </dd>
          </div>

          <div className={`-body-item -item_edit`}>
            <dt className="-middle">
              <label>
                <span className="-span">メールマガジン</span>
              </label>
            </dt>
            <dd>
              <div className="form-col -col">
                <div className="-form-col_item">
                  <h6 className="header-new">
                    {t("features.notification.kaigiHotTopics")}
                  </h6>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="sendenkaigiInfo"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.sendenkaigiInfo}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.sendenkaigiInfo")}
                    className="-form-label-item"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="classInfo"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.classInfo}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.classInfo")}
                    className="-form-label-item"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="contestsAwards"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.contestsAwards}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.contestsAwards")}
                    className="-form-label-item"
                  />
                  <h6 className="-header-new">編集部からのBreaking News</h6>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="advertimes"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.advertimes}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.adverTimesCheckBox")}
                    className="-form-label-item"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="digitalMagazine"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.digitalMagazine}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.magazineCheckBox")}
                    className="-form-label-item"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="editorialSendenkaigi"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.editorialSendenkaigi}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.editorialSendenkaigi")}
                    className="-form-label-item"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="editorialHansokukaigi"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.editorialHansokukaigi}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.editorialHansokukaigi")}
                    className="-form-label-item"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="editorialKohokaigi"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.editorialKohokaigi}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.editorialKohokaigi")}
                    className="-form-label-item"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="editorialBrain"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.editorialBrain}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.editorialBrain")}
                    className="-form-label-item"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="editorialBooks"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.editorialBooks}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.editorialBooks")}
                    className="-form-label-item"
                  />
                  <h6 className="-header-new">Additional Information</h6>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="adPlacementSponsorship"
                        onChange={(e) =>
                          setCheckboxValues((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.checked,
                          }))
                        }
                        checked={checkboxValues.adPlacementSponsorship}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: "16px",
                          },
                          "&.Mui-checked": {
                            color: "#009999",
                          },
                        }}
                        size="small"
                      />
                    }
                    label={t("features.notification.adPlacementSponsorship")}
                    className="-form-label-item"
                  />
                  {/* <ErrorMsg
                                    error={errors.occupationCode}
                                    className="invalid-danger"
                                  /> */}
                </div>
              </div>
            </dd>
          </div>

          {/* <div className="mt-4">
            <h3 className="font-bold mb-2">メールマガジン受信設定</h3>
            <p className="text-sm text-gray-600 mb-2">配信を希望するメールマガジンを選択してください</p>
            {categories.map((item) => (
              <label key={item.id} className="flex items-center gap-2 mb-1">
                <input
                  type="checkbox"
                  checked={selectedCategoryIds.includes(item.id)}
                  onChange={() =>
                    setSelectedCategoryIds((prev) =>
                      prev.includes(item.id)
                        ? prev.filter((id) => id !== item.id)
                        : [...prev, item.id]
                    )
                  }
                />
                {item.label}
              </label>
            ))}
          </div> */}
          {/* <div className="">
            
          
          </div> */}
          <Box
            className="agreement-form-group agreement-fields"
            sx={{
              padding: "20px 15px",
            }}
          >
            <Typography
              className="text-danger"
              sx={{
                fontWeight: 700,
                fontSize: 14,
                marginBottom: 2,
              }}
            >
              {t("features.register.check_terms_msg")}
            </Typography>
            <Box marginBottom={3}>
              <Typography fontSize={14} sx={{ marginBottom: 1 }}>
                {t("features.register.terms_of_use")}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: "230px",
                }}
              >
                <div className="agreement-field agreement-business">
                  <dl className="agreement-content">
                    <div>
                      <div className="agreement-information">
                        <div>
                          <h3>1. 事業者の名称</h3>
                          <p>
                            株式会社 宣伝会議
                            <br />
                            〒107-8550　東京都港区南青山3-11-13 新青山東急ビル9F
                            <br />
                            代表取締役　高橋　智哉
                          </p>
                          <h3>
                            2.管理者（若しくはその代理人）の氏名又は職名、所属及び連絡先
                          </h3>
                          <p>
                            管理者名: 執行役員
                            <br />
                            所属部署: 株式会社 宣伝会議 営業本部
                            <br />
                            連絡先: 電話03-3475-3010
                            <br />
                            個人情報の取扱体制や講じている措置の内容:
                            <br />
                            当社では、個人情報保護管理者を任命し、全社的な体制で個人情報の取扱いを行っています。また、プライバシーマーク認定を取得しJISQ15001の要求事項に準じた措置を講じています。
                          </p>
                          <h3>3. 個人情報の利用目的</h3>
                          <h3>お客様情報</h3>
                          <p>
                            （1）お客様の氏名、住所、電話番号をご購入いただいた商品の発送、アフターサービスの提供のために利用します。
                            <br />
                            （2）ウェビナー、教育講座オンライン動画の視聴データを分析し当社サービスの改善のために利用します。
                            <br />
                            （3）お客様の当社ウェブサイトの閲覧履歴、購買履歴を蓄積して分析し、以下の目的で利用します。
                            <br />
                            ・お客様に最適な商品をウェブサイト上でご提案するため
                            <br />
                            ・当社および当社の提携先の商品・サービスのご案内のため
                            （4）お問合せ等に対する回答のために利用します。
                            <h3>
                              株主情報：氏名、住所、電話番号等の連絡先を以下の目的で使用します。
                            </h3>
                            <p>情報公開、配布物の送付、連絡のため</p>
                            <h3>
                              取引先情報：氏名、会社名、取引情報、住所、電話番号等の連絡先を以下の目的で使用します。
                            </h3>
                            <p>
                              商談及び業務上の諸連絡、受発注業務、請求支払業務のため
                            </p>
                            <h3>社員情報</h3>
                            <p>
                              （1）氏名、電話番号、住所、メールアドレス等を業務連絡、業務管理、セキュリティ管理のために利用します。
                              <br />
                              （2）業務実績、経歴、資格、人事考課等の情報、家族情報等を蓄積、分析し、当社規程に基づき、報酬の決定、人事考課、配属先の決定のために利用します。
                              <br />
                              （3）健康診断結果、身体測定記録、診断書等により、健康的な就業状態の確保、労働者による適正な健康管理のため、病気療養による長期休暇への対応のために利用します。
                            </p>
                            <h3>採用応募者に関する個人情報</h3>
                            <p>
                              <br />
                              （1）氏名、電話番号、住所、メールアドレス等を採用応募者への連絡と当社の採用業務管理のために利用します。
                              <br />
                              （2）経歴、資格、面接結果等を採用の検討のために利用します。
                            </p>
                            <h3>
                              データベース事業者から入手した情報やインターネットなど公開されている個人情報
                            </h3>
                            <p>
                              以下の目的で本人にアクセスすることがあります
                              <br />
                              ・商品、サービスに関する情報の提供および提案のため
                              <br />
                              ・雑誌・書籍、セミナー、その他イベントに関する案内等のため
                              <br />
                              ・その他、有益と思われる情報の提供のため
                            </p>
                            <p>
                              個人情報を集計し、あるいは個人が識別・特定できないように加工したものを作成して、分析や研究、新規サービスの開発に利用する場合があります。個人を特定できない統計データについては、弊社は制限なく利用します。
                            </p>
                          </p>
                          <h3>4. 個人情報の第三者提供</h3>
                          <p>
                            ご記入いただいた個人情報は、法令に基づく場合のほか、弊社または各セミナーおよびイベントを協力あるいは主催する企業が、それぞれのプライバシーポリシーの定める要領にしたがって、第三者又は外部委託先に提供する場合がございます。
                          </p>
                          <h3>5. 個人情報取扱いの委託</h3>
                          <p>
                            当社は事業運営上、お客様により良いサービスを提供するために業務の一部を外部に委託しています。業務委託先に対しては、個人情報を預けることがあります。この場合、個人情報を適切に取り扱っていると認められる委託先を選定し、契約等において個人情報の適正管理・機密保持などによりお客様の個人情報の漏洩防止に必要な事項を取決め、適切な管理を実施させます。
                          </p>
                          <h3>6. 個人情報の開示等の請求</h3>
                          <p>
                            お客様は、当社に対してご自身の個人情報の開示等（利用目的の通知、開示、内容の訂正・追加・削除、利用の停止または消去、第三者への提供の停止、第三者提供記録の開示）に関して、当社問合わせ窓口に申し出ることができます。その際、当社はお客様ご本人を確認させていただいたうえで、合理的な期間内に対応いたします。開示等の申し出の詳細につきましては、当社ホームページ掲載の「保有個人データの開示請求手続きについて」をご覧ください。
                          </p>
                          <h3>7. 個人情報を提供されることの任意性について</h3>
                          <p>
                            お客様が当社に個人情報を提供されるかどうかは、お客様の任意によるものです。ただし、必要な項目をいただけない場合、各サービス等が適切な状態で提供できない場合があります。
                          </p>
                          <h3>
                            8. クッキーその他の個人を特定できない情報について
                          </h3>
                          <p>
                            当社は、当社ウェブサイトの利便性向上、品質向上や行動ターゲティング広告を目的に、ウェブサイトの利用状況を把握するためにクッキー（Cookie）を使用しております。収集された情報は、個人が特定されない形で分析されます。詳細については別に定めるインフォマティブデータの取扱いについてをご参照ください。
                            <br />
                            クッキー等の個人関連情報については、第三者に提供する場合があります。その際、提供先において、個人情報として取得する可能性があります。提供先において、個人関連情報の提供を受けて本人が識別される個人として情報として取得することに同意の上ご利用ください。
                            <br />
                            尚、インターネット利用者が使用するコンピュータ（ブラウザ）では、その設定を変更することにより、当サービスが発行したクッキーを削除することができます。また、設定を変更することにより、クッキーの機能を無効にすることができます。ただし、その結果、ウェブサイト上のサービスの全部または一部が利用できなくなる場合がありますので、あらかじめご了承ください。
                          </p>
                          <h3>9. 情報の共同利用</h3>
                          当社は、キャリナビ利用者および企業の求人求職の結合可能性を高める目的のため、当社グループ会社である「株式会社マスメディアン」（有料職業紹介事業者
                          厚生労働大臣許可：13-ユ-040475）との業務提携契約に基づき、下記の方法等で個人情報を共同利用することがあります。
                          <br />
                          利用目的：両社の利用者および企業の間の求人求職マッチングを高めるため（当社またはマスメディアンのサービスの案内を含む）、就職講座、スキルアップ講座、雑誌・書籍の情報等の案内のため
                          <br />
                          共同利用者の範囲：株式会社マスメディアン
                          <br />
                          利用・取得方法：当社が直接取得した個人情報および求人情報等を、専用通信回線によるデータベースを閲覧する方法等によって共同利用する
                          <br />
                          共同利用における個人情報の管理責任者：
                          <br />
                          株式会社宣伝会議
                          <br />
                          〒107-8550　東京都港区南青山3-11-13 新青山東急ビル9F
                          <br />
                          代表取締役　 高橋　智哉
                          <br />
                          共同利用する個人情報項目：マスナビ利用者の登録データ
                          <h3>10. 認定個人情報保護団体について</h3>
                          <p>
                            当社が加入する認定個人情報保護団体は、ありません。
                          </p>
                          <h3>11. 利用者様自ら開示・記入した情報について</h3>
                          <p>
                            株式会社宣伝会議の各サイトの中には、チャット（会話）やフォーラム（会議室）、掲示板、ニュース・グループ、コメント投稿、電子メール交換などのサービスを提供する場合があります。こうしたサービス内でご利用者様自ら開示・記入した情報は公開情報となりますので、ご注意ください。
                          </p>
                          <h3>12. 外部リンクについて</h3>
                          <p>
                            株式会社宣伝会議の各サイトは、外部のサイトへのリンクを含んでいる場合があります。リンク先サイトでの個人情報の取り扱いについては、当該リンク先サイトにてご自身でご確認ください。このような外部ウェブサイトにおいての個人情報保護の取扱いやサイトの内容に関して株式会社宣伝会議では責任を負いかねますのでご注意ください。
                          </p>
                          <h3>
                            13. 保有個人データの安全管理のために講じた措置
                          </h3>
                          <p>
                            1）基本方針の策定
                            <br />
                            保有個人データの適正な取扱いの確保のため、「関係法令・ガイドライン等の遵守」、「質問及び苦情処理の窓口」等について「個人情報保護方針」を策定しています。
                            <br />
                            2）保有個人データの取扱いに係る規律の整備
                            <br />
                            保有個人データは取得、利用、保存、提供、削除・廃棄等の段階ごとに、取扱方法、責任者・担当者及びその任務等について個人情報保護規程を策定しています。
                            <br />
                            3）組織的安全管理措置
                            <br />
                            ①保有個人データの取扱いに関する責任者を設置するとともに、保有個人データを取り扱う従業者及び当該従業者が取り扱う保有個人データの範囲を明確化し、法や取扱規程に違反している事実又は兆候を把握した場合の責任者への報告連絡体制を整備しています。
                            <br />
                            ②保有個人データの取扱状況について、定期的に自己点検を実施するとともに、他部署や外部の者による監査を実施しています。
                            <br />
                            4）人的安全管理措置
                            <br />
                            ①保有個人データの取扱いに関する留意事項について、従業者に定期的な研修を実施しています。
                            <br />
                            ②保有個人データを含む秘密保持に関する誓約書の提出を全従業者から受けています。
                            <br />
                            5）物理的安全管理措置
                            <br />
                            ①保有個人データを取り扱う区域において、従業者の入退室管理及び持ち込む機器等の制限を行うとともに、権限を有しない者による保有個人データの閲覧を防止する措置を講じています。
                            <br />
                            ②保有個人データを取り扱う機器、電子媒体及び書類等の盗難又は紛失等を防止するための措置を講じるとともに、事業所内の移動を含め、当該機器、電子媒体等を持ち運ぶ場合、容易に保有個人データが判明しないよう措置を講じています。
                            <br />
                            6）技術的安全管理措置
                            <br />
                            ①アクセス制御を実施して、担当者及び取り扱う個人情報データベース等の範囲を限定しています。
                            <br />
                            ②保有個人データを取り扱う情報システムを外部からの不正アクセス又は不正ソフトウェアから保護する仕組みを導入しています。
                            <br />
                            7）外部環境の把握
                            <br />
                            当社が外国において個人データを取り扱う場合、当該外国の個人情報の保護に関する制度等を把握した上で、個人データの安全管理のために必要かつ適切な措置を講じます。
                          </p>
                          <p>
                            【お問合せ窓口】
                            <br />
                            お客様の個人情報に関するお問合せにつきましては、下記窓口で受付けております。
                            <br />
                            〒107-8550 東京都港区南青山3-11-13 新青山東急ビル9F
                            <br />
                            株式会社 宣伝会議 個人情報問い合わせ係
                            <br />
                            メールアドレス:compliance@sendenkaigi.co.jp
                            <br />
                            TEL:03-3475-3010 （受付時間 9:00～18:00※）
                            <br />
                            ※土・日曜日、祝日、年末年始、ゴールデンウィーク期間は翌営業日以降の対応とさせていただきます。
                          </p>
                        </div>
                      </div>
                    </div>
                  </dl>
                </div>
              </Box>
            </Box>
            <hr />
            <Box>
              <Typography fontSize={14} sx={{ marginBottom: 1 }}>
                {t("features.register.personal_information")}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: "230px",
                }}
              >
                <div className="agreement-field agreement-business">
                  <dl className="agreement-content">
                    <div>
                      <div className="agreement-information">
                        <div>
                          <h3>1.事業者の名称</h3>
                          <p>株式会社 宣伝会議</p>
                          <h3>
                            2.個人情報保護管理者（若しくはその代理人）の氏名又は職名、所属及び連絡先
                          </h3>
                          <p>
                            管理者名: 個人情報保護管理責任者
                            コンプライアンス室　室長
                            <br /> 所属：株式会社 宣伝会議 総務部
                            <br /> 連絡先：電話 03-3475-3010
                          </p>
                          <h3>3.個人情報の利用目的</h3>
                          <p>
                            ・会員様向けのサービス提供のため（本人への連絡も含む）
                            <br />
                            ・当社の各種サービスおよびサービスに関連した各種情報のご案内のため
                            <br />
                            ・会員様情報の管理のため
                          </p>
                          <h3>4.個人情報取扱いの委託</h3>
                          <p>
                            当社は事業運営上、前項利用目的の範囲に限って個人情報を外部に委託することがあります。
                          </p>
                          <h3>5.個人情報の開示等の請求</h3>
                          <p>
                            ご本人様は、当社に対してご自身の個人情報の開示等（利用目的の通知、開示、内容の訂正・追加・削除、利用の停止または消去、第三者への提供の停止）に関して、下記の当社問合わせ窓口に申し出ることができます。その際、当社はお客様ご本人を確認させていただいたうえで、合理的な期間内に対応いたします。{" "}
                          </p>
                          <h3>6.個人情報を提供されることの任意性について</h3>
                          <p>
                            {" "}
                            ご本人様が当社に個人情報を提供されるかどうかは任意によるものです。ただし、必要な項目をいただけない場合、適切な対応ができない場合があります。
                          </p>
                        </div>
                      </div>
                    </div>
                  </dl>
                </div>
              </Box>
            </Box>
          </Box>
          <div>
            <div className="form-items">
              <div className="form-item agree_message">
                <div>
                  <dt className="fi-label">
                    <label>
                      <span className="text">利用規約の確認</span>
                      <span className="item_required">
                        {t("general.required")}
                      </span>
                    </label>
                  </dt>
                  <dd className="fi-control">
                    <FormControlLabel
                      ref={checkboxRef}
                      control={
                        <Checkbox
                          inputRef={checkboxRef}
                          sx={{
                            padding: 0,
                            "& .MuiSvgIcon-root": { fontSize: 17 },
                            "&.Mui-checked": {
                              color: "#009999",
                            },
                          }}
                          checked={isCheck}
                          onChange={() => {
                            setState((prevState) => ({
                              ...prevState,
                              isCheck: !isCheck,
                            }));
                          }}
                          // onKeyDown={}
                          // onKeyDown={handleKeyDown}
                        />
                      }
                      label={t("features.register.agree_message")}
                      sx={{
                        color: "#313534",
                        marginTop: "10px",
                        gap: "0.5rem",
                        marginBottom: {
                          xs: "13px",
                          md: "18px",
                        },
                        mx: "auto",
                        alignSelf: "center",
                        "& .MuiFormControlLabel-label": {
                          fontSize: { xs: "0.875rem", md: "0.875rem" },
                          fontWeight: 500,
                        },
                      }}
                    />
                  </dd>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="buttons">
              <button
                type="submit"
                // onClick={handleSubmit}
                disabled={!isCheck}
                className="btn-submit"
              >
                <span>送信</span>
              </button>
            </div>
            <div className="to-login">
              <Link to={routes.LOGIN}>
                <span>すでに会員の方</span>
                <span>
                  <SolidArrow />
                </span>
              </Link>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
};

export default RegisterFormSimple;
